import { useRef } from 'react';
import type { PendingFile } from './FilePreview';
import { FILE_ACCEPT_STRING } from '../config/fileUpload';

interface FilesDrawerProps {
  isOpen: boolean;
  onToggle: () => void;
  files: PendingFile[];
  onRemove: (id: string) => void;
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

// Paperclip icon for ear
const PaperclipIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
  </svg>
);

// Close icon
const CloseIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Trash icon for delete
const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

function getFileIcon(type: string): string {
  if (type.startsWith('image/')) return '🖼️';
  if (type.includes('pdf')) return '📄';
  if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) return '📊';
  if (type.includes('dwg') || type.includes('dxf') || type.includes('svg')) return '📐';
  return '📎';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FilesDrawer({
  isOpen,
  onToggle,
  files,
  onRemove,
  onFilesSelected,
  disabled = false,
}: FilesDrawerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const uploadedCount = files.filter(f => f.status === 'uploaded').length;
  const hasFiles = files.length > 0;

  const handleAddFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []).slice(0, 5);
    const maxSize = 25 * 1024 * 1024;
    const validFiles = selectedFiles.filter((f) => f.size <= maxSize);

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
    e.target.value = '';
  };

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        accept={FILE_ACCEPT_STRING}
      />

      {/* Files ear - positioned above language ear */}
      <button
        onClick={onToggle}
        className={`
          absolute right-0 bottom-[11rem] z-40
          w-6 h-8 rounded-l-md shadow-md
          flex items-center justify-center
          text-sm cursor-pointer
          transition-all duration-200
          hover:w-7 hover:shadow-lg
          ${hasFiles
            ? 'bg-blue-500 text-white'
            : 'bg-white border border-r-0 border-gray-200 text-gray-600 hover:text-gray-800'
          }
          ${uploadingCount > 0 ? 'animate-pulse' : ''}
          ${uploadedCount > 0 && !isOpen ? 'ring-2 ring-blue-400 ring-offset-1 animate-pulse' : ''}
        `}
        aria-label="Files"
        title={hasFiles ? `${files.length} file(s)` : 'Attach files'}
      >
        {uploadingCount > 0 ? (
          <span className="text-xs font-bold">{uploadingCount}</span>
        ) : hasFiles ? (
          <span className="text-xs font-bold">{files.length}</span>
        ) : (
          <PaperclipIcon />
        )}
      </button>

      {/* Files drawer panel */}
      <div
        className={`
          absolute bottom-[7rem] right-0 w-64 max-h-80
          bg-white border border-gray-200 rounded-l-lg shadow-xl
          transform transition-transform duration-300 ease-out z-30
          flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <h3 className="text-xs font-medium text-gray-900">
            Attached Files {hasFiles && `(${files.length})`}
          </h3>
          <button
            onClick={onToggle}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {files.length === 0 ? (
            <div className="text-center py-4 text-xs text-gray-500">
              <p className="mb-2">No files attached</p>
              <p className="text-gray-400">
                Drag & drop files onto the chat
                <br />or click the button below
              </p>
            </div>
          ) : (
            files.map((pf) => (
              <div
                key={pf.id}
                className={`
                  flex items-center gap-2 px-2 py-1.5 rounded text-xs
                  ${pf.status === 'error' ? 'bg-red-50' : 'bg-gray-50'}
                `}
              >
                <span className="text-base">{getFileIcon(pf.file.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{pf.file.name}</div>
                  <div className="text-gray-500 flex items-center gap-1">
                    <span>{formatFileSize(pf.file.size)}</span>
                    {pf.status === 'uploading' && pf.progress !== undefined && (
                      <span className="text-blue-600">• {pf.progress}%</span>
                    )}
                    {pf.status === 'uploaded' && (
                      <span className="text-green-600">• Uploaded</span>
                    )}
                    {pf.status === 'error' && (
                      <span className="text-red-600">• Failed</span>
                    )}
                  </div>
                  {/* Progress bar for uploading files */}
                  {pf.status === 'uploading' && pf.progress !== undefined && (
                    <div className="w-full h-1 bg-gray-200 rounded mt-1">
                      <div
                        className="h-full bg-blue-500 rounded transition-all duration-200"
                        style={{ width: `${pf.progress}%` }}
                      />
                    </div>
                  )}
                </div>
                {/* Delete button */}
                <button
                  onClick={() => onRemove(pf.id)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                  aria-label={`Delete ${pf.file.name}`}
                  title="Delete file"
                >
                  <TrashIcon />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer with add button */}
        <div className="px-3 py-2 border-t border-gray-100">
          <button
            onClick={handleAddFiles}
            disabled={disabled}
            className="w-full px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Files
          </button>
        </div>
      </div>
    </>
  );
}
